// ---------- ce qui casse chez les autres ----------
//
// Le jeu tourne sur des téléphones qu'on n'a pas. Une exception y fige l'écran — le calque de
// lancement est opaque et ne se retire qu'à la fin de `start()`, donc une erreur pendant le
// démarrage laisse un fond mûre uni pour toujours — et personne ne l'apprend : il n'y a rien
// derrière la WebView pour le dire. Pendant le test fermé, douze personnes le racontaient ; en
// production elles désinstallent.
//
// Ce module est le seul canal qui reste. Il fait deux choses, et rien d'autre :
//
//  1. il JOURNALISE — message, fichier, ligne, pile, version — vers le Worker, qui l'écrit dans
//     ses logs (cf. backend/src/routes/clientErrors.ts) ;
//  2. il MONTRE quelque chose quand l'erreur est arrivée avant que le jeu soit prêt, parce qu'un
//     écran gelé n'offre même pas de recharger.
//
// CE QU'IL N'ENVOIE PAS, et c'est la moitié du sujet : ni jeton, ni pseudo, ni identifiant de
// compte, ni progression, ni rien qui vienne du joueur. Un plantage n'a pas besoin de savoir QUI
// jouait, et la politique de confidentialité décrit exactement ces champs-là (public/privacy.html,
// section Diagnostics) — les élargir ici oblige à la relire, cf. PLAY.md §2.1.
//
// IMPORTÉ EN DEUXIÈME dans main.ts, juste après la feuille de style et avant tout le reste : les
// modules ES sont évalués dans l'ordre des imports, donc les gestionnaires posés ici couvrent
// l'évaluation de tous les autres modules et tout le corps de `start()`. Pas en PREMIER : le CSS
// doit garder cette place, splash.ts lit les jetons de la charte dès son évaluation et compte
// dessus. Reste hors de portée le bundle qui ne se charge pas du tout — il faudrait un script en
// clair dans index.html, au prix d'y recopier l'adresse du serveur et le numéro de version.

import { reportClientError } from './api';
import { t } from './i18n';

/**
 * Au plus cinq rapports par session, et jamais deux fois le même.
 *
 * Sans ce plafond, une exception dans la boucle de rendu — soixante images par seconde — envoie
 * soixante requêtes par seconde depuis chaque appareil touché : on se coupe soi-même le serveur
 * en essayant de savoir pourquoi le jeu plante. Cinq suffisent à voir la même signature revenir
 * de plusieurs appareils, ce qui est la seule chose qu'on cherche ici.
 */
const MAX_REPORTS = 5;
let sent = 0;
const seen = new Set<string>();

/** Vrai jusqu'à ce que `start()` soit allé au bout. Ce qui casse avant mérite un écran, ce qui
 *  casse après est presque toujours sans conséquence visible (une animation qui s'arrête, un
 *  compteur qui ne bouge plus) et une fenêtre par-dessus la feuille serait pire que le défaut. */
let booting = true;

/** Appelé à la dernière ligne de `start()` : à partir d'ici, on journalise en silence. */
export function bootDone(): void {
  booting = false;
}

/** Ce qu'on garde d'une pile : les premières lignes disent où, les suivantes disent comment on y
 *  est arrivé et pèsent dix fois plus. Tronqué ici plutôt que côté serveur — ce qui n'est pas
 *  envoyé ne peut pas être stocké par erreur. */
function shorten(v: unknown, max: number): string | undefined {
  if (typeof v !== 'string' || !v) return undefined;
  return v.length > max ? `${v.slice(0, max)}…` : v;
}

function tell(msg: unknown, src?: unknown, line?: unknown, col?: unknown, stack?: unknown): void {
  const message = shorten(typeof msg === 'string' ? msg : String(msg), 300) ?? 'unknown error';
  // La signature ne prend PAS la pile : deux appels du même défaut par des chemins différents
  // sont le même défaut, et on n'a que cinq rapports à dépenser.
  const sig = `${message}|${String(src ?? '')}|${String(line ?? '')}`;
  if (seen.has(sig)) return;
  seen.add(sig);
  if (sent < MAX_REPORTS) {
    sent++;
    reportClientError({
      msg: message,
      src: shorten(src, 200),
      line: typeof line === 'number' ? line : undefined,
      col: typeof col === 'number' ? col : undefined,
      stack: shorten(stack, 1500),
      // Le `versionName` du build.gradle, celui que Play affiche (cf. vite.config.ts). Vide sur
      // le web, et c'est déjà une information : le défaut ne vient alors pas de l'APK.
      version: __VERSION__,
      // La seule chose qui distingue « le jeu n'a jamais démarré » de « il a cassé en route »,
      // et c'est la première question qu'on se pose en lisant un rapport.
      when: booting ? 'boot' : 'play',
    });
  }
  if (booting) showBootFailure();
}

/**
 * L'écran de secours. En STYLES EN LIGNE, ce qui est un choix : ce panneau existe précisément
 * pour les cas où quelque chose n'a pas fonctionné, et une classe CSS suppose que la feuille de
 * style a été chargée. Posé PAR-DESSUS le calque de lancement plutôt qu'à sa place — le retirer
 * demanderait splash.ts, qui est peut-être le module qui vient d'échouer.
 *
 * Une seule fois : cinq erreurs au démarrage ne font pas cinq panneaux.
 */
let panelShown = false;
function showBootFailure(): void {
  if (panelShown || typeof document === 'undefined' || !document.body) return;
  panelShown = true;
  // Le dictionnaire est chargé bien avant (import statique, cf. l'en-tête), mais ce chemin-ci
  // s'exécute justement quand quelque chose n'est pas dans l'état prévu : on retombe sur
  // l'anglais plutôt que d'échouer dans le gestionnaire d'échec.
  let title = 'The game could not start.';
  let retry = 'Retry';
  try {
    title = t('crash.boot');
    retry = t('home.empty.retry');
  } catch {
    /* dictionnaire indisponible : les deux replis anglais ci-dessus */
  }
  const box = document.createElement('div');
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;flex-direction:column;' +
    'align-items:center;justify-content:center;gap:18px;padding:24px;text-align:center;' +
    'background:#ded3f7;color:#1c1830;font:600 17px/1.4 system-ui,sans-serif';
  const p = document.createElement('p');
  p.textContent = title;
  p.style.cssText = 'margin:0;max-width:26ch';
  const b = document.createElement('button');
  b.textContent = retry;
  b.style.cssText =
    'border:0;border-radius:14px;padding:12px 22px;font:inherit;cursor:pointer;' +
    'background:#1c1830;color:#fbf6ec';
  b.onclick = () => location.reload();
  box.append(p, b);
  document.body.appendChild(box);
}

// Le filet posé dans index.html se retire : à partir d'ici, c'est ce module qui parle, avec la
// version, la traduction et la déduplication. Le garde ne sert qu'au cas où l'on n'arrive jamais
// jusqu'à cette ligne — un bundle qui ne se lie pas, une syntaxe que la WebView refuse.
const guard = (window as unknown as { __bootGuard?: { off: boolean } }).__bootGuard;
if (guard) guard.off = true;

// Les deux portes par lesquelles une erreur non rattrapée sort d'une page, et il en faut bien
// deux : `error` ne voit pas les promesses, `unhandledrejection` ne voit que celles-là. Le jeu
// lance beaucoup de choses sans les attendre (les vignettes, la synchronisation, les annonces),
// donc la seconde n'est pas un supplément de zèle.
addEventListener('error', (e) => {
  // Une ressource qui ne charge pas (une image, une police) émet aussi `error`, mais sur SA
  // cible et sans message. Ce n'est pas un plantage et ça n'a pas à consommer un rapport.
  if (!(e instanceof ErrorEvent)) return;
  tell(e.message, e.filename, e.lineno, e.colno, e.error?.stack);
});

addEventListener('unhandledrejection', (e) => {
  const r: unknown = e.reason;
  const msg = r instanceof Error ? r.message : String(r);
  tell(
    `unhandled rejection: ${msg}`,
    undefined,
    undefined,
    undefined,
    r instanceof Error ? r.stack : undefined,
  );
});
